/**
 * Mic capture for Sampler Author / transport Record — ScriptProcessor float tap → mono PCM.
 * Fails soft when insecure / permission denied.
 */
export type MicRecorder = {
  stop: () => Float32Array;
  /** Per-block RMS (0..~1) for meters. */
  getLevel: () => number;
};

export async function startMicCapture(ctx: AudioContext): Promise<MicRecorder> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Microphone capture is not available in this browser");
  }
  if (ctx.state === "suspended") {
    await ctx.resume();
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });
  const source = ctx.createMediaStreamSource(stream);
  const chunks: Float32Array[] = [];
  let level = 0;

  // ScriptProcessor is deprecated but universally available and fine for offline capture.
  const bufferSize = 4096;
  const processor = ctx.createScriptProcessor(bufferSize, 1, 1);
  processor.onaudioprocess = event => {
    const input = event.inputBuffer.getChannelData(0);
    const copy = new Float32Array(input.length);
    copy.set(input);
    chunks.push(copy);
    let sumSq = 0;
    for (let i = 0; i < input.length; i++) {
      const s = input[i]!;
      sumSq += s * s;
    }
    level = Math.sqrt(sumSq / Math.max(1, input.length));
    // Keep graph alive; mute monitor.
    event.outputBuffer.getChannelData(0).fill(0);
  };

  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(ctx.destination);

  return {
    getLevel() {
      return level;
    },
    stop() {
      try {
        processor.disconnect();
        source.disconnect();
        mute.disconnect();
      } catch {
        // ignore
      }
      for (const track of stream.getTracks()) {
        track.stop();
      }
      let total = 0;
      for (const c of chunks) {
        total += c.length;
      }
      const out = new Float32Array(total);
      let offset = 0;
      for (const c of chunks) {
        out.set(c, offset);
        offset += c.length;
      }
      return out;
    },
  };
}
